import React, { useState, useEffect } from 'react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


// Updated DialogStyles to match the consistent format
const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
    zIndex: 1100, // Higher than parent dialog
    paddingTop: '20px', // Add padding from top
    paddingBottom: '20px' // Add padding from bottom
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '700px', // Increased from 600px to match other dialogs
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxHeight: 'calc(100vh - 40px)', // Changed from 90vh to calc(100vh - 40px) to account for padding
    overflowY: 'auto', // Ensure scrolling is enabled
    position: 'relative' // Add position relative
  }
};

const CopyIntegrationFieldDialog = ({
  isOpen,
  onClose,
  field,
  corporateAccount,
  currentProject,
  login_token,
  isProjectLevel,
  systemType,
  systemId,
  selectedRequirements
}) => {
  const [newFieldName, setNewFieldName] = useState('');
  const [targetLevel, setTargetLevel] = useState(isProjectLevel ? 'ProjectLevel' : 'IntegrationLevel');
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Determine the source integration ID (if any)
  const sourceIntegrationId = isProjectLevel ? null : Array.from(selectedRequirements)[0];

  // Default target integration ID based on selected level and current selection
  const [targetIntegrationId, setTargetIntegrationId] = useState(
    !isProjectLevel ? Array.from(selectedRequirements)[0] : null
  );

  // Initialize field name when dialog opens
  useEffect(() => {
    if (isOpen && field) {
      setNewFieldName(`${field.field_name}`);
      setError(''); // Reset error when dialog opens
    }
  }, [isOpen, field]);

  // Fetch statuses when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
    }
  }, [isOpen]);

  const fetchStatuses = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: "REQUIREMENT",
          search_query: ""
        })
      });

      const data = await response.json();
      if (data.status === 'Success' && data.entity_statuses) {
        setStatuses(data.entity_statuses);
        // Set default selected status if available
        if (data.entity_statuses.length > 0) {
          setSelectedStatus(data.entity_statuses[0].status);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching statuses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyField = async () => {
    if (!newFieldName.trim()) {
      setError('New field name is required');
      return;
    }

    if (!selectedStatus) {
      setError('Status is required');
      return;
    }

    setIsLoading(true);
    setError('');

    // For Integration Level target, we need a target integration ID
    if (targetLevel === 'IntegrationLevel' && !targetIntegrationId) {
      setError('No integration is selected for target field');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/copy_integration_requirement_field'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          from_project_id: currentProject,
          from_system_id: systemId,
          from_system_type: systemType,
          from_field_name: field.field_name,
          from_integration_id: sourceIntegrationId, // null for project level
          to_project_id: currentProject,
          to_field_name: newFieldName.trim(),
          to_integration_id: targetLevel === 'IntegrationLevel' ? targetIntegrationId : null,
          to_status: selectedStatus
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // First show the success message (same pattern as other dialogs)
        setError('Field copied successfully');

        // After a delay, close the dialog
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to copy field');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error copying field:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        {/* Updated header to match the consistent format */}
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Copy Integration Field ({field.field_name})
        </h2>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            New Field Name: <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="text"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            disabled={isLoading}
            required
          />
        </div>

        {/* Source Info (Read-only) */}
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '4px'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>Source Field Information:</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <span style={{ fontWeight: 'bold' }}>Project:</span>
            <span>{currentProject}</span>

            <span style={{ fontWeight: 'bold' }}>Level:</span>
            <span>{isProjectLevel ? 'Project Level' : 'Integration Level'}</span>

            {!isProjectLevel && (
              <>
                <span style={{ fontWeight: 'bold' }}>Integration ID:</span>
                <span>{sourceIntegrationId}</span>
              </>
            )}

            <span style={{ fontWeight: 'bold' }}>Field Name:</span>
            <span>{field.field_name}</span>
          </div>
        </div>

        {/* Target options */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Copy to Level: <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Project Level is always available as a target */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="targetLevel"
                style={{ width: '16px', height: '16px' }}
                checked={targetLevel === 'ProjectLevel'}
                onChange={() => {
                  setTargetLevel('ProjectLevel');
                  setTargetIntegrationId(null);
                }}
                disabled={isLoading}
              />
              <span style={{ fontSize: '14px' }}>Project Level</span>
            </label>

            {/* Integration Level only available if we have an integration selected */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              opacity: (!selectedRequirements || selectedRequirements.size !== 1) ? 0.6 : 1
            }}>
              <input
                type="radio"
                name="targetLevel"
                style={{ width: '16px', height: '16px' }}
                checked={targetLevel === 'IntegrationLevel'}
                onChange={() => {
                  setTargetLevel('IntegrationLevel');
                  setTargetIntegrationId(selectedRequirements && selectedRequirements.size === 1 ?
                    Array.from(selectedRequirements)[0] : null);
                }}
                disabled={!selectedRequirements || selectedRequirements.size !== 1 || isLoading}
              />
              <span style={{ fontSize: '14px' }}>
                {!selectedRequirements || selectedRequirements.size !== 1
                  ? 'Integration Level (No integration selected)'
                  : `Integration Level (ID: ${Array.from(selectedRequirements)[0]})`}
              </span>
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Status: <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            disabled={isLoading}
            required
          >
            {statuses.length === 0 ? (
              <option value="">Loading statuses...</option>
            ) : (
              statuses.map(status => (
                <option key={status.status} value={status.status}>
                  {status.status}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Updated error display to match the consistent format from UpdateStatusDialog */}
        {error && (
          <div style={{
            marginBottom: '15px',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
            color: error.includes('successfully') ? '#059669' : '#dc2626',
            border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
          }}>
            {error}
          </div>
        )}

        {/* Updated button section to match the consistent format */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <button
            onClick={() => onClose(false)}
            style={{
              backgroundColor: '#FFFDD0',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <button
            onClick={handleCopyField}
            style={{
              padding: '8px 16px',
              backgroundColor: '#C8F4DE',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center'
            }}
            disabled={isLoading}
          >
            {isLoading && (
              <svg style={{
                animation: 'spin 1s linear infinite',
                marginLeft: '-4px',
                marginRight: '8px',
                width: '16px',
                height: '16px'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isLoading ? 'Copying...' : 'Copy Field'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyIntegrationFieldDialog;